import { HttpParams } from '@angular/common/http';

import type { CarRentalSearchForm } from '../interfaces';

export interface CarRentalDeepLinkOptions {
  apiUrl: string;
  clientId: string;
  locale: string;
  residencyId: string;
}

const pad = (value: number): string => String(value).padStart(2, '0');

const toDateTime = (date: Date, time: string): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${time}`;

export const carRentalDeepLink = (form: CarRentalSearchForm, options: CarRentalDeepLinkOptions): string => {
  const { apiUrl, clientId, locale, residencyId } = options;
  const dropoffLocation = form.differentDropoff && form.dropoffLocation ? form.dropoffLocation : form.pickupLocation;

  const params: Record<string, string> = {
    clientId,
    residencyId,
    age: String(form.driverAge),
    ...(form.pickupLocation ? { pickupIATACode: form.pickupLocation.locationCode } : {}),
    ...(dropoffLocation ? { returnIATACode: dropoffLocation.locationCode } : {}),
    ...(form.pickupDate ? { pickupDateTime: toDateTime(form.pickupDate, form.pickupTime) } : {}),
    ...(form.dropoffDate ? { returnDateTime: toDateTime(form.dropoffDate, form.dropoffTime) } : {}),
  };

  const httpParams = Object.entries(params).reduce((acc, [key, value]) => acc.set(key, value), new HttpParams());

  return `${apiUrl}/${locale}/book?${httpParams.toString()}`;
};

const DEFAULT_RESIDENCY = 'FI';

const CT_RESIDENCY_COUNTRIES = new Set<string>([
  'AD',
  'AE',
  'AG',
  'AI',
  'AL',
  'AM',
  'AN',
  'AO',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AZ',
  'BA',
  'BB',
  'BE',
  'BG',
  'BH',
  'BJ',
  'BL',
  'BN',
  'BO',
  'BR',
  'BS',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CD',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CV',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DO',
  'EC',
  'EE',
  'EG',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GH',
  'GI',
  'GP',
  'GR',
  'GT',
  'GU',
  'GY',
  'HK',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IN',
  'IQ',
  'IS',
  'IT',
  'JM',
  'JO',
  'JP',
  'KE',
  'KH',
  'KN',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LK',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MD',
  'ME',
  'MF',
  'MG',
  'MK',
  'MO',
  'MP',
  'MQ',
  'MT',
  'MU',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PR',
  'PT',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RW',
  'SA',
  'SC',
  'SE',
  'SG',
  'SI',
  'SK',
  'SN',
  'SR',
  'SV',
  'SZ',
  'TC',
  'TG',
  'TH',
  'TN',
  'TR',
  'TT',
  'TW',
  'TZ',
  'UA',
  'UG',
  'US',
  'UY',
  'UZ',
  'VC',
  'VE',
  'VI',
  'VN',
  'VU',
  'WS',
  'XK',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
]);

export const resolveResidency = (countryCode: string | null | undefined, fallback = DEFAULT_RESIDENCY): string => {
  const code = (countryCode ?? '').trim().toUpperCase();
  return CT_RESIDENCY_COUNTRIES.has(code) ? code : fallback;
};
